const chapter = {
  id: 'conic-sections',
  slug: 'conic-sections',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE Main + Advanced',
  title: {
    english: 'Conic Sections',
        hindi: 'शंकु परिच्छेद',
  },
  estReadMinutes: 70,
  difficulty: {
    english: 'Medium-Hard',
        hindi: 'मध्यम-कठिन',
  },
  weightage: {
    english: '2–3 questions in JEE Main, 1–2 in Advanced',
        hindi: 'जेईई मेन में 2-3 प्रश्न, एडवांस्ड में 1-2 प्रश्न',
  },
  syllabusTags: ['Coordinate Geometry', 'JEE-Main', 'JEE-Advanced', 'Class-11'],
  sections: [
    {
      id: 'overview',
      title: {
        english: 'What is a Conic Section?',
        hindi: 'शंकु परिच्छेद क्या है?',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Conic',
        hindi: 'शंकुधर',
          },
          body: {
            english: 'A **conic section** is the locus of a point $P$ which moves so that the ratio of its distance from a fixed point (the **focus** $S$) to its perpendicular distance from a fixed line (the **directrix**) is a constant $e$, called the **eccentricity**.',
        hindi: 'एक **शंक्वाकार खंड** एक बिंदु $P$ का स्थान है जो इस प्रकार चलता है कि एक निश्चित बिंदु (**फोकस** $S$) से इसकी दूरी और एक निश्चित रेखा (**डायरेक्ट्रिक्स**) से इसकी लंबवत दूरी का अनुपात एक स्थिरांक $e$ होता है, जिसे **विलक्षणता** कहा जाता है।',
          },
        },
        {
          type: 'table',
          headers: ['Conic', 'Eccentricity', 'Section of cone'],
          rows: [
            ['Circle', '$e = 0$', 'Plane perpendicular to axis'],
            ['Parabola', '$e = 1$', 'Plane parallel to a generator'],
            ['Ellipse', '$0 < e < 1$', 'Plane cuts only one nappe'],
            ['Hyperbola', '$e > 1$', 'Plane cuts both nappes'],
          ],
        },
        {
          type: 'note',
          text: {
            english: `General second-degree equation $ax^2 + 2hxy + by^2 + 2gx + 2fy + c = 0$ represents:
• **Pair of lines** if $\\Delta = 0$ (where $\\Delta$ is the $3\\times3$ determinant) and $h^2 \\ge ab$;
• **Circle** if $a = b,\\ h = 0$;
• **Parabola** if $h^2 = ab$;
• **Ellipse** if $h^2 < ab$;
• **Hyperbola** if $h^2 > ab$.`,
            hindi: 'सामान्य द्वितीय-डिग्री समीकरण $ax^2 + 2hxy + by^2 + 2gx + 2fy + c = 0$ दर्शाता है: • **पंक्तियों का युग्म** यदि $\\Delta = 0$ (जहां $\\Delta$ $3\\times3$ निर्धारक है) और $h^2 \\ge ab$; • **वृत्त** यदि $a = b,\\\\\\\\ h = 0$; • **परवलय** यदि $h^2 = ab$; • **दीर्घवृत्त** यदि $h^2 < ab$; • **हाइपरबोला** यदि $h^2 > ab$।',
          },
        },
      ],
    },
    {
      id: 'parabola',
      title: {
        english: 'Parabola',
        hindi: 'परवलय',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Standard form (opens right): $y^2 = 4ax$. Vertex at origin, axis along $x$-axis.',
        hindi: 'मानक प्रपत्र (दाईं ओर खुलता है): $y^2 = 4ax$। मूल पर शीर्ष, $x$-अक्ष के अनुदिश अक्ष।',
          },
        },
        {
          type: 'figure',
          svg: `
<svg viewBox="0 0 380 260" xmlns="http://www.w3.org/2000/svg">
  <line x1="20" y1="130" x2="360" y2="130" stroke="#9ca3af" stroke-width="1"/>
  <line x1="90" y1="20" x2="90" y2="240" stroke="#9ca3af" stroke-width="1"/>
  <polyline points="140.6,220.0 136.2,216.0 132.0,212.0 128.0,208.0 124.2,204.0 120.6,200.0 117.2,196.0 114.0,192.0 111.0,188.0 108.2,184.0 105.6,180.0 103.2,176.0 101.0,172.0 99.0,168.0 97.2,164.0 95.6,160.0 94.2,156.0 93.0,152.0 92.0,148.0 91.2,144.0 90.6,140.0 90.2,136.0 90.0,132.0 90.0,128.0 90.2,124.0 90.6,120.0 91.2,116.0 92.0,112.0 93.0,108.0 94.2,104.0 95.6,100.0 97.2,96.0 99.0,92.0 101.0,88.0 103.2,84.0 105.6,80.0 108.2,76.0 111.0,72.0 114.0,68.0 117.2,64.0 120.6,60.0 124.2,56.0 128.0,52.0 132.0,48.0 136.2,44.0 140.6,40.0" fill="none" stroke="#4338ca" stroke-width="2.2"/>
  <line x1="50" y1="20" x2="50" y2="240" stroke="#dc2626" stroke-dasharray="5 4" stroke-width="1.4"/>
  <text x="-10" y="40" font-size="11" fill="#b91c1c">directrix x = −a</text>
  <circle cx="130" cy="130" r="4" fill="#10b981"/>
  <text x="136" y="124" font-size="12" fill="#047857">F(a, 0)</text>
  <text x="94" y="144" font-size="11" fill="#1f2937">V</text>
  <text x="190" y="245" font-size="12" fill="#6b7280" text-anchor="middle">y² = 4ax  (vertex at V, focus F, directrix x = −a)</text>
</svg>`,
          caption: {
            english: 'Parabola y² = 4ax: vertex V, focus F(a, 0), directrix x = −a.',
        hindi: 'परवलय y² = 4ax: शीर्ष V, फोकस F(a, 0), नियता x = −a.',
          },
        },
        {
          type: 'table',
          headers: ['Element', '$y^2 = 4ax$', '$y^2 = -4ax$', '$x^2 = 4ay$', '$x^2 = -4ay$'],
          rows: [
            ['Vertex', '$(0, 0)$', '$(0, 0)$', '$(0, 0)$', '$(0, 0)$'],
            ['Focus', '$(a, 0)$', '$(-a, 0)$', '$(0, a)$', '$(0, -a)$'],
            ['Directrix', '$x = -a$', '$x = a$', '$y = -a$', '$y = a$'],
            ['Axis', '$y = 0$', '$y = 0$', '$x = 0$', '$x = 0$'],
            ['Latus rectum', '$4a$', '$4a$', '$4a$', '$4a$'],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Parametric form** of $y^2 = 4ax$:  $(at^2, 2at)$.',
        hindi: '**$y^2 = 4ax$ का पैरामीट्रिक फॉर्म**: $(at^2, 2at)$।',
            },
            {
              english: '**Focal distance** from $(at^2, 2at)$ to focus $= a + at^2 = a(1 + t^2)$ — equals $x$-coordinate $+ a$.',
        hindi: '**फोकल दूरी** $(at^2, 2at)$ से फोकस $= a + at^2 = a(1 + t^2)$ तक - $x$-निर्देशांक $+ a$ के बराबर होती है।',
            },
            {
              english: '**Tangent at $(x_1, y_1)$**:  $yy_1 = 2a(x + x_1)$.',
        hindi: '**$(x_1, y_1)$** पर स्पर्शरेखा: $yy_1 = 2a(x + x_1)$।',
            },
            {
              english: '**Tangent in slope form**:  $y = mx + a/m$ (touches $y^2 = 4ax$).',
        hindi: '**ढलान रूप में स्पर्शरेखा**: $y = mx + a/m$ ($y^2 = 4ax$ को छूती है)।',
            },
            {
              english: '**Normal at $(at^2, 2at)$**:  $y + tx = 2at + at^3$.',
        hindi: '**$(at^2, 2at)$** पर सामान्य: $y + tx = 2at + at^3$।',
            },
            {
              english: '**Chord of contact** from external $(x_1, y_1)$:  $yy_1 = 2a(x + x_1)$.',
        hindi: '**संपर्क का तार** बाहरी $(x_1, y_1)$ से: $yy_1 = 2a(x + x_1)$।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Find focus and directrix of $y^2 = 12x$.',
        hindi: '$y^2 = 12x$ का फोकस और डायरेक्ट्रिक्स खोजें।',
          },
          solution: {
            english: '$4a = 12 \\Rightarrow a = 3$. Focus $(3, 0)$, directrix $x = -3$.',
        hindi: '$4ए = 12 \\Rightarrow ए = 3$। फोकस $(3, 0)$, डायरेक्ट्रिक्स $x = -3$।',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Find equation of tangent to $y^2 = 8x$ with slope $2$.',
        hindi: 'ढलान $2$ के साथ $y^2 = 8x$ की स्पर्श रेखा का समीकरण ज्ञात कीजिए।',
          },
          solution: {
            english: '$a = 2$. Tangent in slope form: $y = mx + a/m = 2x + 1$.',
        hindi: '$a = 2$। ढलान रूप में स्पर्शरेखा: $y = mx + a/m = 2x + 1$।',
          },
        },
      ],
    },
    {
      id: 'ellipse',
      title: {
        english: 'Ellipse',
        hindi: 'दीर्घवृत्त',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Standard form: $\\dfrac{x^2}{a^2} + \\dfrac{y^2}{b^2} = 1$ with $a > b > 0$.',
        hindi: 'मानक रूप: $\\dfrac{x^2}{a^2} + \\dfrac{y^2}{b^2} = 1$ $a > b > 0$ के साथ।',
          },
        },
        {
          type: 'figure',
          svg: `
<svg viewBox="0 0 380 240" xmlns="http://www.w3.org/2000/svg">
  <line x1="20" y1="120" x2="360" y2="120" stroke="#9ca3af" stroke-width="1"/>
  <line x1="190" y1="20" x2="190" y2="220" stroke="#9ca3af" stroke-width="1"/>
  <ellipse cx="190" cy="120" rx="130" ry="70" fill="none" stroke="#4338ca" stroke-width="2.2"/>
  <circle cx="80.5" cy="120" r="4" fill="#dc2626"/>
  <circle cx="299.5" cy="120" r="4" fill="#dc2626"/>
  <text x="52.45548849896677" y="112" font-size="11" fill="#b91c1c">F₁(−c,0)</text>
  <text x="305.54451150103324" y="112" font-size="11" fill="#b91c1c">F₂(c,0)</text>
  <text x="324" y="134" font-size="11" fill="#1f2937">(a,0)</text>
  <text x="194" y="46" font-size="11" fill="#1f2937">(0,b)</text>
  <text x="190" y="225" font-size="12" fill="#6b7280" text-anchor="middle">x²/a² + y²/b² = 1   (a > b),  c² = a² − b²,  e = c/a</text>
</svg>`,
          caption: {
            english: 'Ellipse with major axis along x-axis: foci F₁(−c, 0), F₂(c, 0); c² = a² − b²; e = c/a.',
        hindi: 'एक्स-अक्ष के साथ प्रमुख अक्ष के साथ दीर्घवृत्त: foci F₁(−c, 0), F₂(c, 0); c² = a² − b²; ई = सी/ए.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Foci**:  $(\\pm c, 0)$ where $c^2 = a^2 - b^2$.',
        hindi: '**फ़ोकस**: $(\\pm c, 0)$ जहां $c^2 = a^2 - b^2$।',
            },
            {
              english: '**Eccentricity**:  $e = c/a < 1$. Equivalently $b^2 = a^2(1 - e^2)$.',
        hindi: '**विलक्षणता**: $e = c/a < 1$। समान रूप से $b^2 = a^2(1 - e^2)$.',
            },
            {
              english: '**Vertices**:  $(\\pm a, 0)$.   **Co-vertices**:  $(0, \\pm b)$.',
        hindi: '**शीर्ष**: $(\\pm a, 0)$. **सह-शीर्ष**: $(0, \\pm b)$।',
            },
            {
              english: '**Directrices**:  $x = \\pm a/e$.',
        hindi: '**निर्देश**: $x = \\pm a/e$.',
            },
            {
              english: '**Latus rectum**:  $\\dfrac{2b^2}{a}$ (length).',
        hindi: '**लैटस रेक्टम**: $\\dfrac{2b^2}{a}$ (लंबाई)।',
            },
            {
              english: '**Sum of focal distances** from any point on ellipse to two foci $= 2a$.',
        hindi: '**दीर्घवृत्त पर किसी भी बिंदु से दो फोकसों तक की फोकल दूरियों का योग** $= 2a$।',
            },
            {
              english: '**Parametric form**:  $(a\\cos\\theta,\\ b\\sin\\theta)$.',
        hindi: '**पैरामीट्रिक फॉर्म**: $(a\\cos\\theta,\\ b\\sin\\theta)$.',
            },
            {
              english: '**Auxiliary circle**:  $x^2 + y^2 = a^2$.',
        hindi: '**सहायक वृत्त**: $x^2 + y^2 = a^2$.',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Tangent and normal',
        hindi: 'स्पर्शरेखा और सामान्य',
          },
          statement: {
            english: `**Tangent at $(x_1, y_1)$**:  $\\dfrac{xx_1}{a^2} + \\dfrac{yy_1}{b^2} = 1$.

**Tangent in slope form**:  $y = mx \\pm \\sqrt{a^2 m^2 + b^2}$.

**Normal at $(x_1, y_1)$**:  $\\dfrac{a^2 x}{x_1} - \\dfrac{b^2 y}{y_1} = a^2 - b^2$.`,
            hindi: '**$(x_1, y_1)$** पर स्पर्शरेखा: $\\dfrac{xx_1}{a^2} + \\dfrac{yy_1}{b^2} = 1$। **ढलान रूप में स्पर्शरेखा**: $y = mx \\pm \\sqrt{a^2 m^2 + b^2}$। **$(x_1, y_1)$** पर सामान्य: $\\dfrac{a^2 x}{x_1} - \\dfrac{b^2 y}{y_1} = a^2 - b^2$। **$(x_1, y_1)$** पर स्पर्शरेखा: $\\dfrac{xx_1}{a^2} + \\dfrac{yy_1}{b^2} = 1$। **ढलान रूप में स्पर्शरेखा**: $y = mx \\pm \\sqrt{a^2 m^2 + b^2}$। **$(x_1, y_1)$** पर सामान्य: $\\dfrac{a^2 x}{x_1} - \\dfrac{b^2 y}{y_1} = a^2 - b^2$।',
          },
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'Find foci and eccentricity of $\\dfrac{x^2}{25} + \\dfrac{y^2}{9} = 1$.',
        hindi: '$\\dfrac{x^2}{25} + \\dfrac{y^2}{9} = 1$ की नाभियाँ और विलक्षणता ज्ञात कीजिए।',
          },
          solution: {
            english: '$a = 5, b = 3,\\ c = \\sqrt{25 - 9} = 4$. Foci $(\\pm 4, 0)$, $e = 4/5$.',
        hindi: '$a = 5, b = 3,\\ c = \\sqrt{25 - 9} = 4$. Foci $(\\pm 4, 0)$, $e = 4/5$.',
          },
        },
      ],
    },
    {
      id: 'hyperbola',
      title: {
        english: 'Hyperbola',
        hindi: 'अतिपरवलय',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Standard form: $\\dfrac{x^2}{a^2} - \\dfrac{y^2}{b^2} = 1$.',
        hindi: 'मानक रूप: $\\dfrac{x^2}{a^2} - \\dfrac{y^2}{b^2} = 1$.',
          },
        },
        {
          type: 'figure',
          svg: `
<svg viewBox="0 0 380 260" xmlns="http://www.w3.org/2000/svg">
  <line x1="20" y1="130" x2="360" y2="130" stroke="#9ca3af" stroke-width="1"/>
  <line x1="190" y1="20" x2="190" y2="240" stroke="#9ca3af" stroke-width="1"/>
  <line x1="20" y1="-96.7" x2="360" y2="356.7" stroke="#10b981" stroke-dasharray="4 4" stroke-width="1"/>
  <line x1="20" y1="356.7" x2="360" y2="-96.7" stroke="#10b981" stroke-dasharray="4 4" stroke-width="1"/>
  <polyline points="355.6,335.8 340.2,313.7 327.7,295.3 317.3,279.7 308.5,266.3 301.0,254.6 294.6,244.3 289.0,235.1 284.2,226.8 279.9,219.3 276.1,212.4 272.8,206.0 269.8,200.2 267.2,194.7 264.8,189.6 262.7,184.7 260.8,180.2 259.1,175.8 257.6,171.6 256.3,167.7 255.1,163.8 254.1,160.1 253.2,156.5 252.4,153.0 251.8,149.6 251.2,146.2 250.8,142.9 250.4,139.6 250.2,136.4 250.0,133.2 250.0,130.0 250.0,126.8 250.2,123.6 250.4,120.4 250.8,117.1 251.2,113.8 251.8,110.4 252.4,107.0 253.2,103.5 254.1,99.9 255.1,96.2 256.3,92.3 257.6,88.4 259.1,84.2 260.8,79.8 262.7,75.3 264.8,70.4 267.2,65.3 269.8,59.8 272.8,54.0 276.1,47.6 279.9,40.7 284.2,33.2 289.0,24.9 294.6,15.7 301.0,5.4 308.5,-6.3 317.3,-19.7 327.7,-35.3 340.2,-53.7" fill="none" stroke="#4338ca" stroke-width="2.2"/>
  <polyline points="24.4,335.8 39.8,313.7 52.3,295.3 62.7,279.7 71.5,266.3 79.0,254.6 85.4,244.3 91.0,235.1 95.8,226.8 100.1,219.3 103.9,212.4 107.2,206.0 110.2,200.2 112.8,194.7 115.2,189.6 117.3,184.7 119.2,180.2 120.9,175.8 122.4,171.6 123.7,167.7 124.9,163.8 125.9,160.1 126.8,156.5 127.6,153.0 128.2,149.6 128.8,146.2 129.2,142.9 129.6,139.6 129.8,136.4 130.0,133.2 130.0,130.0 130.0,126.8 129.8,123.6 129.6,120.4 129.2,117.1 128.8,113.8 128.2,110.4 127.6,107.0 126.8,103.5 125.9,99.9 124.9,96.2 123.7,92.3 122.4,88.4 120.9,84.2 119.2,79.8 117.3,75.3 115.2,70.4 112.8,65.3 110.2,59.8 107.2,54.0 103.9,47.6 100.1,40.7 95.8,33.2 91.0,24.9 85.4,15.7 79.0,5.4 71.5,-6.3 62.7,-19.7 52.3,-35.3 39.8,-53.7" fill="none" stroke="#4338ca" stroke-width="2.2"/>
  <text x="190" y="245" font-size="12" fill="#6b7280" text-anchor="middle">x²/a² − y²/b² = 1,  asymptotes y = ±(b/a)x,  e = √(1 + b²/a²)</text>
</svg>`,
          caption: {
            english: 'Hyperbola x²/a² − y²/b² = 1 with two branches and asymptotes y = ±(b/a)x.',
        hindi: 'हाइपरबोला x²/a² - y²/b² = 1 दो शाखाओं और अनंतस्पर्शी y = ±(b/a)x के साथ।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Foci**:  $(\\pm c, 0)$ where $c^2 = a^2 + b^2$.',
        hindi: '**फ़ोकस**: $(\\pm c, 0)$ जहां $c^2 = a^2 + b^2$।',
            },
            {
              english: '**Eccentricity**:  $e = c/a > 1$. Equivalently $b^2 = a^2(e^2 - 1)$.',
        hindi: '**विलक्षणता**: $e = c/a > 1$। समतुल्य $b^2 = a^2(e^2 - 1)$.',
            },
            {
              english: '**Vertices**:  $(\\pm a, 0)$.',
        hindi: '**शीर्ष**: $(\\pm a, 0)$.',
            },
            {
              english: '**Directrices**:  $x = \\pm a/e$.',
        hindi: '**निर्देश**: $x = \\pm a/e$.',
            },
            {
              english: '**Asymptotes**:  $y = \\pm (b/a)x$, i.e. $\\dfrac{x^2}{a^2} - \\dfrac{y^2}{b^2} = 0$.',
        hindi: '**स्पर्शी लक्षण**: $y = \\pm (b/a)x$, अर्थात $\\dfrac{x^2}{a^2} - \\dfrac{y^2}{b^2} = 0$।',
            },
            {
              english: '**Latus rectum**:  $\\dfrac{2b^2}{a}$.',
        hindi: '**लैटस रेक्टम**: $\\dfrac{2b^2}{a}$.',
            },
            {
              english: '**Difference of focal distances** $= 2a$.',
        hindi: '**फोकल दूरियों का अंतर** $= 2a$.',
            },
            {
              english: '**Parametric form**:  $(a\\sec\\theta,\\ b\\tan\\theta)$.',
        hindi: '**पैरामीट्रिक फॉर्म**: $(a\\sec\\theta,\\ b\\tan\\theta)$.',
            },
            {
              english: '**Conjugate hyperbola** of $\\dfrac{x^2}{a^2} - \\dfrac{y^2}{b^2} = 1$ is $\\dfrac{x^2}{a^2} - \\dfrac{y^2}{b^2} = -1$.',
        hindi: '$\\dfrac{x^2}{a^2} - \\dfrac{y^2}{b^2} = 1$ का **संयुग्मित अतिपरवलय** $\\dfrac{x^2}{a^2} - \\dfrac{y^2}{b^2} = -1$ है।',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Tangent and normal',
        hindi: 'स्पर्शरेखा और सामान्य',
          },
          statement: {
            english: `**Tangent at $(x_1, y_1)$**:  $\\dfrac{xx_1}{a^2} - \\dfrac{yy_1}{b^2} = 1$.

**Tangent in slope form**:  $y = mx \\pm \\sqrt{a^2 m^2 - b^2}$.

**Normal at $(x_1, y_1)$**:  $\\dfrac{a^2 x}{x_1} + \\dfrac{b^2 y}{y_1} = a^2 + b^2$.`,
            hindi: '**$(x_1, y_1)$** पर स्पर्शरेखा: $\\dfrac{xx_1}{a^2} - \\dfrac{yy_1}{b^2} = 1$। **ढलान रूप में स्पर्शरेखा**: $y = mx \\pm \\sqrt{a^2 m^2 - b^2}$। **$(x_1, y_1)$** पर सामान्य: $\\dfrac{a^2 x}{x_1} + \\dfrac{b^2 y}{y_1} = a^2 + b^2$।',
          },
        },
        {
          type: 'definition',
          term: {
            english: 'Rectangular Hyperbola',
        hindi: 'आयताकार अतिपरवलय',
          },
          body: {
            english: 'A hyperbola with $a = b$ is called **rectangular** (or equilateral). Its eccentricity is $e = \\sqrt 2$ and asymptotes are $y = \\pm x$. Rotating axes by $45°$ gives the form $xy = c^2$.',
        hindi: '$a = b$ वाले हाइपरबोला को **आयताकार** (या समबाहु) कहा जाता है। इसकी विलक्षणता $e = \\sqrt 2$ है और स्पर्शोन्मुखता $y = \\pm x$ है। अक्षों को $45°$ द्वारा घुमाने से $xy = c^2$ रूप मिलता है।',
          },
        },
        {
          type: 'example',
          number: 4,
          problem: {
            english: 'Find eccentricity and foci of $\\dfrac{x^2}{16} - \\dfrac{y^2}{9} = 1$.',
        hindi: '$\\dfrac{x^2}{16} - \\dfrac{y^2}{9} = 1$ की विलक्षणता और फोकस ज्ञात करें।',
          },
          solution: {
            english: '$a = 4, b = 3,\\ c = \\sqrt{16 + 9} = 5$. $e = 5/4$, foci $(\\pm 5, 0)$.',
        hindi: '$a = 4, b = 3,\\ c = \\sqrt{16 + 9} = 5$. $e = 5/4$, foci $(\\pm 5, 0)$.',
          },
        },
        {
          type: 'example',
          number: 5,
          problem: {
            english: 'For hyperbola $xy = 16$, find eccentricity.',
        hindi: 'हाइपरबोला $xy = 16$ के लिए, विलक्षणता ज्ञात करें।',
          },
          solution: {
            english: 'Rectangular hyperbola: $e = \\sqrt 2$.',
        hindi: 'आयताकार हाइपरबोला: $e = \\sqrt 2$।',
          },
        },
      ],
    },
    {
      id: 'comparison',
      title: {
        english: 'Comparison of Conics',
        hindi: 'शंकु परिच्छेदों की तुलना',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Property', 'Parabola $y^2 = 4ax$', 'Ellipse $\\tfrac{x^2}{a^2}+\\tfrac{y^2}{b^2}=1$', 'Hyperbola $\\tfrac{x^2}{a^2}-\\tfrac{y^2}{b^2}=1$'],
          rows: [
            ['Eccentricity $e$', '$1$', '$<1$', '$>1$'],
            ['Focus', '$(a, 0)$', '$(\\pm c, 0)$, $c=\\sqrt{a^2-b^2}$', '$(\\pm c, 0)$, $c=\\sqrt{a^2+b^2}$'],
            ['Directrix', '$x = -a$', '$x = \\pm a/e$', '$x = \\pm a/e$'],
            ['Latus rectum', '$4a$', '$2b^2/a$', '$2b^2/a$'],
            ['Tangent (slope $m$)', '$y = mx + a/m$', '$y = mx \\pm \\sqrt{a^2 m^2 + b^2}$', '$y = mx \\pm \\sqrt{a^2 m^2 - b^2}$'],
            ['Parametric', '$(at^2, 2at)$', '$(a\\cos\\theta, b\\sin\\theta)$', '$(a\\sec\\theta, b\\tan\\theta)$'],
          ],
        },
      ],
    },
    {
      id: 'mixed-examples',
      title: {
        english: 'Mixed Worked Examples',
        hindi: 'मिश्रित हल किये गये उदाहरण',
      },
      blocks: [
        {
          type: 'example',
          number: 6,
          problem: {
            english: 'Find the equation of the tangent to the ellipse $\\dfrac{x^2}{9} + \\dfrac{y^2}{4} = 1$ at $(3\\cos\\theta, 2\\sin\\theta)$.',
        hindi: '$(3\\cos\\theta, 2\\sin\\theta)$ पर दीर्घवृत्त $\\dfrac{x^2}{9} + \\dfrac{y^2}{4} = 1$ की स्पर्शरेखा का समीकरण ज्ञात कीजिए।',
          },
          solution: {
            english: 'Use $\\dfrac{xx_1}{a^2} + \\dfrac{yy_1}{b^2} = 1$:  $\\dfrac{x \\cos\\theta}{3} + \\dfrac{y \\sin\\theta}{2} = 1$.',
        hindi: '$\\dfrac{xx_1}{a^2} + \\dfrac{yy_1}{b^2} = 1$: $\\dfrac{x \\cos\\theta}{3} + \\dfrac{y \\sin\\theta}{2} = 1$ का उपयोग करें।',
          },
        },
        {
          type: 'example',
          number: 7,
          problem: {
            english: 'Find the locus of the midpoints of focal chords of $y^2 = 4ax$.',
        hindi: '$y^2 = 4ax$ की फोकल जीवा के मध्यबिंदु का स्थान ज्ञात कीजिए।',
          },
          solution: {
            english: 'Let midpoint be $(h, k)$. Equation of chord with this midpoint: $T = S_1$. For parabola: $ky - 2a(x + h) = k^2 - 4ah$, i.e. $ky = 2ax + (k^2 - 2ah)$. Passes through $(a, 0)$: $0 = 2a^2 + k^2 - 2ah \\Rightarrow k^2 = 2a(h - a)$. Locus: $y^2 = 2a(x - a)$.',
        hindi: 'माना मध्यबिंदु $(h, k)$ है। इस मध्यबिंदु के साथ जीवा का समीकरण: $T = S_1$. परवलय के लिए: $ky - 2a(x + h) = k^2 - 4ah$, यानी $ky = 2ax + (k^2 - 2ah)$। $(a, 0)$ से होकर गुजरता है: $0 = 2a^2 + k^2 - 2ah \\Rightarrow k^2 = 2a(h - a)$। स्थान: $y^2 = 2a(x - a)$।',
          },
        },
        {
          type: 'example',
          number: 8,
          problem: {
            english: 'If line $y = mx + 1$ is tangent to parabola $y^2 = 4x$, find $m$.',
        hindi: 'यदि रेखा $y = mx + 1$ परवलय $y^2 = 4x$ की स्पर्शरेखा है, तो $m$ खोजें।',
          },
          solution: {
            english: 'Tangent slope form: $y = mx + 1/m$. Comparing $1/m = 1 \\Rightarrow m = 1$.',
        hindi: 'स्पर्शरेखा ढलान प्रपत्र: $y = mx + 1/m$। $1/m = 1 \\Rightarrow m = 1$ की तुलना करना।',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'y^2 = 4ax',
      desc: {
        english: 'Standard parabola',
        hindi: 'मानक परवलय',
      },
    },
    {
      latex: '(at^2,\\ 2at)',
      desc: {
        english: 'Parametric point on parabola',
        hindi: 'परवलय पर पैरामीट्रिक बिंदु',
      },
    },
    {
      latex: 'y = mx + a/m',
      desc: {
        english: 'Tangent (slope form) to parabola',
        hindi: 'परवलय की स्पर्शरेखा (ढलान रूप)।',
      },
    },
    {
      latex: '\\tfrac{x^2}{a^2} + \\tfrac{y^2}{b^2} = 1',
      desc: {
        english: 'Ellipse, $b^2 = a^2(1-e^2)$',
        hindi: 'दीर्घवृत्त, $b^2 = a^2(1-e^2)$',
      },
    },
    {
      latex: 'y = mx \\pm \\sqrt{a^2 m^2 + b^2}',
      desc: {
        english: 'Tangent (slope) to ellipse',
        hindi: 'दीर्घवृत्त के लिए स्पर्शरेखा (ढलान)।',
      },
    },
    {
      latex: '\\tfrac{x^2}{a^2} - \\tfrac{y^2}{b^2} = 1',
      desc: {
        english: 'Hyperbola, $b^2 = a^2(e^2-1)$',
        hindi: 'अतिपरवलय, $b^2 = a^2(e^2-1)$',
      },
    },
    {
      latex: 'y = mx \\pm \\sqrt{a^2 m^2 - b^2}',
      desc: {
        english: 'Tangent (slope) to hyperbola',
        hindi: 'हाइपरबोला की स्पर्शरेखा (ढलान)।',
      },
    },
    {
      latex: 'y = \\pm (b/a) x',
      desc: {
        english: 'Asymptotes of standard hyperbola',
        hindi: 'मानक हाइपरबोला के स्पर्शोन्मुख',
      },
    },
    {
      latex: 'xy = c^2,\\ e = \\sqrt 2',
      desc: {
        english: 'Rectangular hyperbola',
        hindi: 'आयताकार अतिपरवलय',
      },
    },
    {
      latex: 'L.R. = 2b^2/a\\ \\text{or}\\ 4a',
      desc: {
        english: 'Latus rectum',
        hindi: 'लैटस रेक्टम',
      },
    },
  ],
  pyqs: [
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'Parabola',
      problem: {
        english: 'The focus of the parabola $x^2 = -8y$ is:',
        hindi: 'परवलय $x^2 = -8y$ का फोकस है:',
      },
      answer: {
        english: '$(0, -2)$',
        hindi: '$(0, -2)$',
      },
      solution: {
        english: 'Form $x^2 = -4ay$ with $4a = 8 \\Rightarrow a = 2$, focus $(0, -a) = (0, -2)$.',
        hindi: 'फॉर्म $x^2 = -4ay$ $4a = 8 \\Rightarrow a = 2$ के साथ, फोकस $(0, -a) = (0, -2)$।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Ellipse',
      problem: {
        english: 'Eccentricity of ellipse $4x^2 + 9y^2 = 36$ is:',
        hindi: 'दीर्घवृत्त की विलक्षणता $4x^2 + 9y^2 = 36$ है:',
      },
      answer: {
        english: '$\\sqrt 5/3$',
        hindi: '$\\sqrt 5/3$',
      },
      solution: {
        english: 'Divide by 36: $x^2/9 + y^2/4 = 1$. $a^2 = 9, b^2 = 4, c^2 = 5, e = \\sqrt 5/3$.',
        hindi: '36 से विभाजित करें: $x^2/9 + y^2/4 = 1$. $a^2 = 9, b^2 = 4, c^2 = 5, e = \\sqrt 5/3$।',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Hyperbola',
      problem: {
        english: 'If $e_1, e_2$ are eccentricities of a hyperbola and its conjugate, then $1/e_1^2 + 1/e_2^2 = ?$',
        hindi: 'यदि $e_1, e_2$ हाइपरबोला और उसके संयुग्म की विलक्षणताएं हैं, तो $1/e_1^2 + 1/e_2^2 = ?$',
      },
      answer: {
        english: '$1$',
        hindi: '$1$',
      },
      solution: {
        english: 'For $\\tfrac{x^2}{a^2} - \\tfrac{y^2}{b^2} = 1$: $e_1^2 = 1 + b^2/a^2$. For conjugate: $e_2^2 = 1 + a^2/b^2$. Sum of reciprocals $= \\dfrac{a^2}{a^2 + b^2} + \\dfrac{b^2}{a^2 + b^2} = 1$.',
        hindi: '$\\tfrac{x^2}{a^2} - \\tfrac{y^2}{b^2} = 1$ के लिए: $e_1^2 = 1 + b^2/a^2$. संयुग्म के लिए: $e_2^2 = 1 + a^2/b^2$। व्युत्क्रमों का योग $= \\dfrac{a^2}{a^2 + b^2} + \\dfrac{b^2}{a^2 + b^2} = 1$.',
      },
    },
    {
      year: 2020,
      exam: 'JEE Advanced',
      tag: 'Parabola tangent',
      problem: {
        english: 'The line $y = mx + c$ is a tangent to $y^2 = 4x$. Find relation between $m$ and $c$.',
        hindi: 'रेखा $y = mx + c$ $y^2 = 4x$ की स्पर्श रेखा है। $m$ और $c$ के बीच संबंध खोजें।',
      },
      answer: {
        english: '$c = 1/m$',
        hindi: '$c = 1/m$',
      },
      solution: {
        english: 'Slope-form tangent of $y^2 = 4ax$ is $y = mx + a/m$ with $a = 1$.',
        hindi: '$y^2 = 4ax$ की ढलान-रूप स्पर्शरेखा $a = 1$ के साथ $y = mx + a/m$ है।',
      },
    },
    {
      year: 2019,
      exam: 'JEE Main',
      tag: 'Ellipse',
      problem: {
        english: 'Length of latus rectum of $\\dfrac{x^2}{16} + \\dfrac{y^2}{9} = 1$:',
        hindi: '$\\dfrac{x^2}{16} + \\dfrac{y^2}{9} = 1$ के लैटस रेक्टम की लंबाई:',
      },
      answer: {
        english: '$9/2$',
        hindi: '$9/2$',
      },
      solution: {
        english: '$2b^2/a = 2 \\cdot 9 / 4 = 9/2$.',
        hindi: '$2बी^2/ए = 2 \\cdot 9/4 = 9/2$।',
      },
    },
  ],
  next: {
    slug: 'limits-continuity-differentiability',
    title: 'Limits, Continuity & Differentiability',
  },
};

export default chapter;
